import React, { FC, useState, useEffect } from "react";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Container,
  Box,
  ButtonBase,
  Typography,
  Modal,
} from "@mui/material";
import { makeStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { Toolbar } from "../components/FooterToolbar";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import DiplomaSupplementCoc from "../components/DiplomaSupplementCoC";
import DiplomaSupplementTicket from "../components/DiplomaSupplementTicket";
import {
  creditPs8ResponseType,
  creditRequestType,
  creditResponseType,
  trainingIndicatorRequestType,
  trainingIndicatorResponseType,
  diplomaSupplementRequestType,
  diplomaSupplementResponseType,
  gpaRequestType,
  gpaResponseType,
  updateDiplomaSupplementRequestType,
} from "../common/type";
import {
  useAuth0Token,
  getDiplomaSupplement,
  updateDiplomaSupplement,
  getCredit,
  getTrainingIndicator,
  getGpaScore,
} from "../common/http-requests";
import { useTranslation } from 'react-i18next';
import { descriptionText } from "../common/Comment";
import { basicColor } from "../common/colors";
import {
  useIsReadOnly,
  useUserId,
  useUserNickname,
  useUserStudentNumber,
  useLanguage,
} from "../hooks/useStoreGetters";

const DefaultStagePoint: creditPs8ResponseType = {
  ps8_credit_1: 0.0,
  ps8_credit_2: 0.0,
  ps8_credit_3: 0.0,
  ps8_credit_4: 0.0,
  ps8_credit_5: 0.0,
  ps8_credit_6: 0.0,
  ps8_credit_7: 0.0,
  ps8_credit_8: 0.0,
};

const DefaultTrainingIndicator: trainingIndicatorResponseType = {
  training_indicators: [120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120, 120],
};

// Material-UIのスタイル設定
const useStyles = makeStyles({
  modalStyle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    border: '2px solid #000',
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    padding: '16px', // p: 4 を padding: '16px' に修正
    backgroundColor: '#ffffff',
  },
  buttonStyle: {
    width: 200,
    height: 50,
    background: basicColor,
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    margin: '0 10px',
  },
  fixedWidthContent: {
    // widthはA4サイズのPDFに、2ページをそれぞれ中央揃えに配置するための数値
    // 小さくすると、PDFに表示される文字サイズを大きくすることができる。ただし、PDF上の１ページ目と２ページ目のコンテンツの幅が異なる
    // 大きくすると、PDF上の１ページ目と２ページ目のコンテンツの幅を揃えられる。ただし、PDFに表示される文字サイズが小さくなる
    width: 2000,
    backgroundColor: 'white',
    padding: 20,
    boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
    '& td, & th': {
      paddingBottom: '20px',
      fontSize: 18,
    },
    '& p': {
      marginBottom: '20px',
      fontSize: 18,
    },
  },
});

const BAR_CHART_SIZE: number[] = [
  1000, 320
];

type DegreeType = 'UG' | 'MS' | 'Prof'; // 学生部 / 大学院・修士課程 / 大学院・専門職学位課程
type EnrollmentStatus = 'enrolled' | 'graduated'; // 在籍中 / 卒業（修了）
interface UserStatusType {
  academicDegree: DegreeType;
  enrollment: EnrollmentStatus;
};

const DiplomaSupplementPage: FC = () => {
  const { t } = useTranslation();
  const [isSending, setIsSending] = useState(true);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const { getToken } = useAuth0Token();
  const isReadOnly = useIsReadOnly();
  const userId = useUserId();
  const userNickname = useUserNickname();
  const userStudentNumber = useUserStudentNumber();
  const language = useLanguage();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();

  const [userStatus, setUserStatus] = useState<UserStatusType>({ academicDegree: 'UG', enrollment: 'enrolled' });
  const [diplomaSupplementData, setDiplomaSupplementData] = useState<diplomaSupplementResponseType>({
    student_number: '',
    user_name: '',
    department_name: '',
    major_name: '',
    course_name: '',
    degree_issued_date: '',
    academic_degree_name: '',
    academic_degree_code: '',
    user_quality: '',
    user_self_refection: '',
    achievement_1: '',
    achievement_2: '',
    achievement_3: '',
    achievement_4: '',
    achievement_8: '',
    admission_date: null,
    graduation_date: null,
  });
  const [creditData, setCreditData] = useState<creditResponseType>({
    previous_credits: DefaultStagePoint,
    latest_credits: DefaultStagePoint,
    base_credits: DefaultStagePoint,
    period_count: 0,
  });// パースペクティブ
  const [trainingIndicatorData, setTrainingIndicatorData] = useState<trainingIndicatorResponseType>(DefaultTrainingIndicator); // 育成指標
  const [gpaData, setGpaData] = useState<gpaResponseType>({
    scoreList: [],
    seriesNameList: [],
  }); // GPA
  const [userSelfRefection, setUserSelfRefection] = useState(""); // 自己評価
  // api取得失敗時のアラート
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  // clickawayした際、api取得失敗時のアラートを行わない。
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  let alreadyFiredOnce = false;
  useEffect(() => {
    (async () => {
      if (!alreadyFiredOnce) {
        setIsSending(true);
        await getDiplomaSupplementData(
          userId,
          userStudentNumber,
          language,
        );
      }
    })();
    return () => {
      alreadyFiredOnce = true;
    };
  }, []);

  const getDegreeType = (academic_degree_code: string): DegreeType => {
    switch (academic_degree_code) {
      case "540":
        return "UG";
      case "600":
      case "610":
        return "MS";
      case "620":
        return "Prof";
      default:
        return "UG";
    }
  };

  const getDiplomaSupplementData = async (
    login_user_id: number, // userResponseType.id の値
    student_number: string,
    language: string,   // "ja" (日本語) or "en" (英語)
  ) => {
    setIsSending(true);
    try {
      const requestParams: diplomaSupplementRequestType = {
        login_user_id,
        language,
      };
      const diplomaSupplementResponseData: diplomaSupplementResponseType = await getDiplomaSupplement(
        await getToken(),
        requestParams
      );
      const requestCreditParams: creditRequestType = {
        login_user_id,
      };
      const creditResponseData: creditResponseType = await getCredit(
        await getToken(),
        requestCreditParams
      );
      // 全国指標のデータを取得
      const requestMextParams: trainingIndicatorRequestType = {
        login_user_id,
        target_type: "mext",
      };
      const mextData: trainingIndicatorResponseType = await getTrainingIndicator(
        await getToken(),
        requestMextParams
      );
      const requestGpaParams: gpaRequestType = {
        login_user_id,
        student_number,
      };
      const gpaScoreData: gpaResponseType = await getGpaScore(
        await getToken(),
        requestGpaParams
      );
      setGpaData(gpaScoreData);
      setTrainingIndicatorData(mextData);
      setCreditData(creditResponseData);
      setDiplomaSupplementData(diplomaSupplementResponseData);
      setUserStatus({
        academicDegree: diplomaSupplementResponseData.academic_degree_code ? getDegreeType(diplomaSupplementResponseData.academic_degree_code) : "UG",
        enrollment: "enrolled",
      });
      setUserSelfRefection(diplomaSupplementResponseData.user_self_refection ?? "");
    } catch (e) {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotLoadData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleChange = (text: string) => {
    setUserSelfRefection(text);
  };

  const handleSave = () => {
    if (isReadOnly) return;
    updateDiplomaSupplementData(userId);
  };

  const updateDiplomaSupplementData = async (
    login_user_id: number, // userResponseType.id の値
  ) => {
    setIsSending(true);
    try {
      const requestParams: updateDiplomaSupplementRequestType = {
        login_user_id,
        user_self_refection: userSelfRefection,
      };
      await updateDiplomaSupplement(
        await getToken(),
        requestParams
      );
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.saved"),
        type: "success",
      });
    } catch (e) {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.cannotSaveData"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  interface jsPDFWithAutoTable extends jsPDF {
    autoTable: (options: any) => void;
  }

  const handlePublish = () => {
    setIsExportingPDF(true);
  }

  useEffect(() => {
    if (isExportingPDF) {
      const delay = 100;
      const timeoutId = setTimeout(() => {
        exportPDF();
      }, delay);
      return () => clearTimeout(timeoutId);
    }
  }, [isExportingPDF]);

  const exportPDF = () => {
    if (!isExportingPDF) {
      return;
    }
    const input1 = document.getElementById('pdf-content-1');
    const input2 = document.getElementById('pdf-content-2');
    if (!input1 || !input2) {
      console.error('Element not found');
      setIsExportingPDF(false);
      return;
    }
    const pdf = new jsPDF('p', 'mm', 'a4') as jsPDFWithAutoTable;
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const margin = 10; // Margin in mm
    const addImageToPDF = (canvas: HTMLCanvasElement, isNewPage: boolean) => {
      const imgData = canvas.toDataURL('image/jpeg', 0.6); // Compress to 60%
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min((pageWidth - margin * 2) / imgWidth, (pageHeight - margin * 2) / imgHeight);
      const scaledWidth = imgWidth * ratio;
      const scaledHeight = imgHeight * ratio;
      if (isNewPage) {
        pdf.addPage();
      }
      pdf.addImage(imgData, 'JPEG', margin, margin, scaledWidth, scaledHeight);
    };
    const formatDateString = (): string => {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      const minute = String(date.getMinutes()).padStart(2, '0');
      const second = String(date.getSeconds()).padStart(2, '0');
      return `${year}${month}${day}_${hour}${minute}${second}`;
    };
    const captureAndAddImage = (element: HTMLElement, isNewPage: boolean) => {
      return html2canvas(element, { scale: 2 }).then((canvas) => {
        addImageToPDF(canvas, isNewPage);
      });
    };
    captureAndAddImage(input1, false)
      .then(() => captureAndAddImage(input2, true))
      .then(() => {
        pdf.save(`diploma_supplement_${formatDateString()}.pdf`, { returnPromise: true })
          .then(() => {
            setIsExportingPDF(false);
          })
          .catch((error) => {
            console.error('Error during PDF download:', error);
            setIsExportingPDF(false);
          });
      });
  };

  const SummaryMessage = styled(Box)(() => ({
    maxWidth: `${BAR_CHART_SIZE[0]}px`,
    margin: isDesktop ? '3.5rem auto 0' : '7.5rem auto 0',
    textAlign: 'center',
  }));

  return (
    <>
      <Container maxWidth="xl">
        {isSending ? (
          <>
            <CircularProgressMolecule displayLabel={t("msg.loading")} />
          </>
        ) : (
          <>
            <VisualizedMenus />
            <FeedbackBar
              feedbackInfo={feedbackInfo}
              handleClose={handleClose}
            ></FeedbackBar>
            <SummaryMessage>{descriptionText(userNickname, language, t, "ds")}</SummaryMessage>
            <Modal
              open={isExportingPDF}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <Box className={classes.modalStyle}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                  {t("msg.publishPdf")}
                </Typography>
                <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                  {t("msg.publishPdf1")}<br />
                  {t("msg.publishPdf2")}
                </Typography>
              </Box>
            </Modal>
            <div id="pdf-content-1" className={isExportingPDF ? classes.fixedWidthContent : ''}>
              <DiplomaSupplementCoc
                userStatus={userStatus}
                userData={diplomaSupplementData}
                creditData={creditData}
                trainingIndicatorData={trainingIndicatorData}
                gpaData={gpaData}
                userSelfRefection={userSelfRefection}
                onChange={handleChange}
                isExportingPDF={isExportingPDF}
                onSaveClick={handleSave}
                isReadOnly={isReadOnly}
              />
            </div>
            <Box
              className="flex flex-row justify-evenly items-center"
              sx={{
                marginTop: '3rem',
                marginBottom: isExportingPDF ? '3rem' : '6rem',
              }}>
              <ButtonBase className={classes.buttonStyle} onClick={handlePublish}>{t("button.publish")}</ButtonBase>
            </Box>
            {isExportingPDF &&
              <div id="pdf-content-2" className={classes.fixedWidthContent}>
                <DiplomaSupplementTicket />
              </div>}
          </>
        )}
        <Toolbar />
      </Container>
    </>
  );
};

export default DiplomaSupplementPage;